import React, { useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { PurchaseOrder, PurchaseOrderItem, HandlingUnit, HandlingUnitStage, HandlingUnitLocation } from '../../types/purchaseOrder';
import { formatDate, formatCurrency } from '../../utils';
import AddHandlingUnitNoteModal from './edit-dialog/AddHandlingUnitNoteModal';

interface PurchaseOrderItemsProps {
  purchaseOrder: PurchaseOrder;
  canUpdateStage: boolean;
  canSetTruckNumber: boolean;
  username: string;
  onUpdateHandlingUnitStage: (huNumber: string, stage: HandlingUnitStage) => Promise<void>;
  onUpdateTruckNumber: (huNumber: string, truckNumber: number | null) => Promise<void>;
  onUpdateTruckDateTime: (huNumber: string, date: Date | null) => Promise<void>;
  onUpdateLocation: (huNumber: string, location: string) => Promise<void>;
  onUpdateReceivedCount: (huNumber: string, receivedCount: number) => Promise<void>;
  onUpdateHandlingUnitNote: (huNumber: string, note: string) => Promise<void>;
}

// Available truck numbers
const TRUCK_NUMBERS: { label: string; value: number | null }[] = [
  { label: 'No Truck', value: null },
  { label: 'Truck 1', value: 1 },
  { label: 'Truck 2', value: 2 }
];

// Available locations
const LOCATIONS: { label: string; value: string }[] = [
  { label: 'Location A', value: 'A' },
  { label: 'Location B', value: 'B' },
  { label: 'Location C', value: 'C' },
  { label: 'Location D', value: 'D' },
  { label: 'Location E', value: 'E' }
];

const PurchaseOrderItems: React.FC<PurchaseOrderItemsProps> = ({
  purchaseOrder,
  canUpdateStage,
  canSetTruckNumber,
  username,
  onUpdateHandlingUnitStage,
  onUpdateTruckNumber,
  onUpdateTruckDateTime,
  onUpdateLocation,
  onUpdateReceivedCount,
  onUpdateHandlingUnitNote
}) => {
  const [selectedHU, setSelectedHU] = useState<HandlingUnit | null>(null);
  const [noteModalVisible, setNoteModalVisible] = useState(false);

  const calculateItemSubtotal = (item: PurchaseOrderItem): number => {
    return parseFloat(item.quantity.toString()) * parseFloat(item.unitPrice.toString());
  };

  const calculateTotalPrice = (items: PurchaseOrderItem[]): number => {
    return items.reduce((total, item) => total + calculateItemSubtotal(item), 0);
  };

  const handleEditNote = (hu: HandlingUnit) => {
    setSelectedHU(hu);
    setNoteModalVisible(true);
  };

  const handleSaveNote = async (note: string) => {
    if (selectedHU) {
      await onUpdateHandlingUnitNote(selectedHU.huNumber, note);
      setNoteModalVisible(false);
      setSelectedHU(null);
    }
  };

  const handlingUnitsTemplate = (item: PurchaseOrderItem) => {
    if (!item.handlingUnits || item.handlingUnits.length === 0) {
      return <span>No handling units</span>;
    }
    return (
      <div>
        {item.handlingUnits.map((hu) => (
          <div key={hu.id} className="mb-2 p-2 border-round surface-100">
            <div className="flex align-items-center mb-2">
              <span className="font-bold mr-2">{hu.huNumber}</span>
              {canUpdateStage && (
                <Dropdown
                  value={hu.stage}
                  options={Object.values(HandlingUnitStage)}
                  onChange={(e) => onUpdateHandlingUnitStage(hu.huNumber, e.value)}
                  className="p-inputtext-sm mr-2"
                />
              )}
              {hu.stage === HandlingUnitStage.Completed && canSetTruckNumber && (
                <div className="flex align-items-center">
                  <Dropdown
                    value={hu.handlingUnitOutboundTruckNumber}
                    options={TRUCK_NUMBERS}
                    onChange={(e) => onUpdateTruckNumber(hu.huNumber, e.value)}
                    placeholder="Select Truck"
                    className="p-inputtext-sm mr-2"
                  />
                  <Calendar
                    value={hu.huTruckDateTime ? new Date(hu.huTruckDateTime) : null}
                    onChange={(e) => onUpdateTruckDateTime(hu.huNumber, e.value as Date)}
                    showTime
                    hourFormat="24"
                    placeholder="Select Date/Time"
                    className="p-inputtext-sm"
                  />
                </div>
              )}
              {hu.stage === HandlingUnitStage.Received && (
                <div className="flex align-items-center">
                  <Dropdown
                    value={hu.location}
                    options={LOCATIONS}
                    onChange={(e) => onUpdateLocation(hu.huNumber, e.value)}
                    placeholder="Select Location"
                    className="p-inputtext-sm mr-2"
                  />
                  <InputNumber
                    value={hu.receivedCount}
                    onValueChange={(e) => onUpdateReceivedCount(hu.huNumber, e.value || 0)}
                    placeholder="Received Count"
                    className="p-inputtext-sm mr-2"
                    min={0}
                    showButtons={false}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {hu.location && (
                <Tag 
                  value={`Location: ${hu.location}`} 
                  severity="info"
                />
              )}
              <div className="w-full flex align-items-center justify-content-between">
                {hu.notes && (
                  <div className="flex-grow-1 mr-2">
                    <div className="text-sm text-500 mb-1">Notes:</div>
                    <div className="p-2 surface-200 border-round text-sm">{hu.notes}</div>
                  </div>
                )}
                <Button
                  icon="pi pi-pencil"
                  label={hu.notes ? "Edit Note" : "Add Note"}
                  onClick={() => handleEditNote(hu)}
                  className="p-button-text p-button-sm"
                />
              </div>
              {hu.receivedCount !== undefined && hu.receivedCount !== null && (
                <Tag 
                  value={`Received: ${hu.receivedCount}`} 
                  severity="success"
                />
              )}
              {hu.handlingUnitOutboundTruckNumber && (
                <>
                  <Tag 
                    value={`Truck: ${hu.handlingUnitOutboundTruckNumber}`} 
                    severity="info"
                  />
                  {hu.huTruckDateTime && (
                    <Tag 
                      value={`Assigned: ${formatDate(new Date(hu.huTruckDateTime))}`} 
                      severity="info"
                    />
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="col-12">
      <h3>Items</h3>
      <DataTable 
        value={purchaseOrder.items} 
        className="mb-3"
        sortField="itemNumber"
        sortOrder={1}
      >
        <Column field="itemNumber" header="Item #" sortable />
        <Column 
          field="product.name" 
          header="Product" 
          body={(item: PurchaseOrderItem) => item.product?.name || 'N/A'}
        />
        <Column field="quantity" header="Quantity" />
        <Column 
          field="unitPrice" 
          header="Unit Price" 
          body={(item: PurchaseOrderItem) => formatCurrency(parseFloat(item.unitPrice.toString()))} 
        />
        <Column 
          field="subtotal" 
          header="Subtotal" 
          body={(item: PurchaseOrderItem) => formatCurrency(calculateItemSubtotal(item))}
        />
        <Column 
          field="handlingUnits" 
          header="Handling Units"
          body={handlingUnitsTemplate}
        />
      </DataTable>
      <div className="text-right font-bold text-xl mb-3">
        Total: {formatCurrency(calculateTotalPrice(purchaseOrder.items))}
      </div>

      <AddHandlingUnitNoteModal
        visible={noteModalVisible}
        onHide={() => setNoteModalVisible(false)}
        onSave={handleSaveNote}
        currentNote={selectedHU?.notes || ''}
      />
    </div>
  );
};

export default PurchaseOrderItems;
